'use client';
import React from 'react';
import {
  Combobox as ChakraComboBox,
  Portal,
  useFilter,
  useListCollection,
  Highlight,
  Span,
  Stack,
  InputGroup,
  Icon,
} from '@chakra-ui/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useRef } from 'react';
import { AiOutlineSearch } from 'react-icons/ai';
import { isPrimitiveList } from '../utils';
import { ComponentPropsWithRef } from 'react';
import { InfoTip, Alert, labelStyles, EmptyStateList } from '.';

export type ComboboxEvent = {
  value: string[] | number[];
  items: any[];
};

export type ComboboxEventSelectPayload =
  | string
  | number
  | string[]
  | number[]
  | undefined
  | {
      value: string | number | string[] | number[] | undefined;
      items: any[] | any | undefined;
    };

type BaseComboboxProps = Omit<
  ComponentPropsWithRef<typeof ChakraComboBox.Root>,
  'onSelect' | 'collection' | 'defaultValue' | 'placeholder'
>;

export interface ComboboxProps extends BaseComboboxProps {
  items: any[];
  isLoading?: boolean;
  id?: string;
  onSelect: (value: ComboboxEventSelectPayload) => void;
  keyForDisplay?: string | null;
  keyForValue?: string | null;
  getEntireItem?: boolean;
  placeholder?: string | null;
  label?: string | null;
  infoTip?: string;
  createDescription?: null | ((item: any) => string);
  multiple?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  filterComponent?: 'startsWith' | 'endsWith' | 'contains';
  onBlur?: () => void;
  startElement?: React.ReactNode;
  endElement?: React.ReactNode;
  defaultValue?: string[] | number[] | string | number;
  useSelectAllOption?: boolean;
  triggerResetToDefault?: number;
  color?: string;
}

export const ComboBox_Inner = React.forwardRef<HTMLDivElement, ComboboxProps>(
  function ComboBox_Inner(
    {
      id,
      items,
      isLoading,
      onSelect,
      keyForDisplay = null,
      keyForValue = null,
      getEntireItem = true,
      placeholder = null,
      label = null,
      infoTip = null,
      createDescription = null,
      multiple = false,
      disabled = false,
      clearable = true,
      filterComponent = 'contains',
      onBlur = () => null,
      startElement = null,
      endElement = null,
      defaultValue = [],
      useSelectAllOption = false, // can be used only if multiple is true
      triggerResetToDefault = 0,
      color,
      ...rest
    },
    ref,
  ) {
    const contentRef = useRef<HTMLDivElement>(null);
    const isPrimitive = isPrimitiveList(items);
    const triggerResetToDefaultRef = React.useRef(triggerResetToDefault);
    const triggerSelectAllRef = React.useRef(false);

    const calculatePlaceholderValue: (selectedItems: any[]) => string =
      React.useCallback(
        (selectedItems: any[]) => {
          if (!selectedItems || selectedItems.length === 0)
            return placeholder || 'Type to search';
          if (selectedItems.length === 1)
            return isPrimitive
              ? String(selectedItems[0])
              : String(selectedItems[0][keyForDisplay!]);
          return `${selectedItems.length} selected`;
        },
        [placeholder, isPrimitive, keyForDisplay],
      );

    // const [isCleared, setIsCleared] = React.useState<boolean>(false);

    // const allItemValues = items.map((item: any) =>
    //   isPrimitive ? String(item) : String(item[keyForValue!]),
    // );

    // const isAllSelected =
    //   allItemValues.length > 0 &&
    //   allItemValues.every((val: string) => value.map(String).includes(val));s

    const { startsWith, endsWith, contains } = useFilter({
      sensitivity: 'base',
    });

    const { collection, set, clear, filter, reset } = useListCollection({
      initialItems: items,
      itemToString: (item) => (isPrimitive ? item : item[keyForDisplay!]),
      itemToValue: (item) =>
        isPrimitive ? String(item) : String(item[keyForValue!]),
      filter:
        filterComponent === 'startsWith'
          ? startsWith
          : filterComponent === 'endsWith'
            ? endsWith
            : contains,

      // limit: 10,
    });

    // React.useEffect(() => {
    //   set(items);
    // }, [items]);

    const {
      normalisedDefaultValue,
      isNumber,
      initialisedPlaceholderValue,
      initialisedInputValue,
      memoDefaultItems,
      isMissingDefaultValue,
    }: {
      normalisedDefaultValue: string[];
      isNumber: boolean;
      initialisedPlaceholderValue: string;
      initialisedInputValue: string | undefined;
      memoDefaultItems: any[];
      isMissingDefaultValue: boolean;
    } = React.useMemo(() => {
      const normalisedDefaultValue = normaliseDefaultValue(defaultValue);

      if (!items || !Array.isArray(items) || items.length <= 0 || isLoading) {
        set([]);
        return {
          normalisedDefaultValue: [],
          isNumber: false,
          initialisedPlaceholderValue: '',
          initialisedInputValue: '',
          memoDefaultItems: [],
          isMissingDefaultValue: false,
        };
      }
      const isNumber = items.some((item) =>
        isPrimitive
          ? typeof item === 'number'
          : typeof item[keyForValue!] === 'number',
      );

      const itemsByValue = new Map<string, any>();
      for (const item of items) {
        const keyValue = isPrimitive
          ? String(item)
          : String(item[keyForValue!]);
        itemsByValue.set(keyValue, item);
      }

      const filteredDefaults: string[] = normalisedDefaultValue.filter(
        (val) => {
          const keyVal = isNumber ? String(Number(val)) : String(val);
          return itemsByValue.has(keyVal);
        },
      );

      const defaultItems =
        !isPrimitive && filteredDefaults.length > 0 && items.length > 0
          ? filteredDefaults.map((val) => {
              const keyVal = isNumber ? String(Number(val)) : String(val);
              return itemsByValue.get(keyVal);
            })
          : filteredDefaults;

      set(Array.isArray(items) ? (items as any[]) : []);

      return {
        normalisedDefaultValue: filteredDefaults,
        isNumber,
        initialisedPlaceholderValue: calculatePlaceholderValue(defaultItems),
        initialisedInputValue:
          filteredDefaults.length === 1 && items.length > 0
            ? !isPrimitive
              ? defaultItems[0]?.[keyForDisplay!]
              : defaultItems[0]
            : undefined,
        memoDefaultItems: Array.isArray(defaultItems)
          ? (defaultItems as any[])
          : [],
        isMissingDefaultValue:
          normalisedDefaultValue.length > 0 &&
          normalisedDefaultValue.length !== filteredDefaults.length,
      };
    }, [items]);

    const [value, setValue] = React.useState<string[]>([]);
    const [inputValue, setInputValue] = React.useState<string | undefined>(
      initialisedInputValue,
    );
    const [placeholderValue, setPlaceholderValue] = React.useState<string>(
      initialisedPlaceholderValue,
    );

    // Update value when normalisedDefaultValue changes and items are loaded
    React.useEffect(() => {
      if (normalisedDefaultValue.length > 0) {
        setValue(normalisedDefaultValue);
        setPlaceholderValue(initialisedPlaceholderValue);
      }
    }, [normalisedDefaultValue]);

    React.useEffect(() => {
      if (isMissingDefaultValue) {
        onSelect(
          getEntireItem
            ? multiple
              ? isNumber
                ? {
                    value: normalisedDefaultValue.map(Number),
                    items: memoDefaultItems,
                  }
                : { value: normalisedDefaultValue, items: memoDefaultItems }
              : isNumber
                ? {
                    value: Number(normalisedDefaultValue[0]) ?? undefined,
                    items: memoDefaultItems[0] ?? undefined,
                  }
                : {
                    value: normalisedDefaultValue[0] ?? undefined,
                    items: memoDefaultItems[0] ?? undefined,
                  }
            : multiple
              ? isNumber
                ? normalisedDefaultValue.map(Number)
                : normalisedDefaultValue
              : isNumber
                ? (Number(normalisedDefaultValue[0]) ?? undefined)
                : (normalisedDefaultValue[0] ?? undefined),
        );
      }
    }, [isMissingDefaultValue]);

    const allItemsSelected =
      multiple &&
      useSelectAllOption &&
      value.length > 0 &&
      items.length > 0 &&
      value.length === items.length &&
      value.every((value) =>
        items.some((item) =>
          isPrimitive
            ? String(item) === value
            : String(item[keyForValue!]) === value,
        ),
      )
        ? true
        : false;

    const handleOnSelect = (values: string[], selectedItems: any[]) => {
      setValue(values);
      onSelect(
        getEntireItem
          ? multiple
            ? isNumber
              ? { value: values.map(Number), items: selectedItems }
              : { value: values, items: items }
            : isNumber
              ? {
                  value: Number(values[0]) ?? undefined,
                  items: selectedItems[0] ?? undefined,
                }
              : {
                  value: values[0] ?? undefined,
                  items: selectedItems[0] ?? undefined,
                }
          : multiple
            ? isNumber
              ? values.map(Number)
              : values
            : isNumber
              ? (Number(values[0]) ?? undefined)
              : (values[0] ?? undefined),
      );
      setInputValue(undefined);
      // Clear the filter and reset to show all items
      filter('');
      setTimeout(() => {
        reset();
        set(items);
      }, 0);
    };

    const handleSelectAll = () => {
      triggerSelectAllRef.current = true;

      if (!allItemsSelected) {
        // Select all
        setPlaceholderValue(`${items.length} selected`);
        handleOnSelect(
          items.map((item: any) =>
            isPrimitive ? String(item) : String(item[keyForValue!]),
          ),
          items,
        );
      } else {
        // Deselect all
        setPlaceholderValue(placeholder || 'Type to search');
        handleOnSelect([], []);
      }
    };

    const handleInputChange = (e: any) => {
      setInputValue(e.inputValue);
      filter(e.inputValue);

      // if (e.inputValue && e.inputValue.length > 0) {
      //   setIsCleared(false);
      // }
    };

    const handleOnChange = (e: ComboboxEvent) => {
      if (!triggerSelectAllRef.current) {
        setPlaceholderValue(calculatePlaceholderValue(e.items));
        handleOnSelect(e.value as string[], e.items);
      } else triggerSelectAllRef.current = false;
    };

    const handleClear = () => {
      // setIsCleared(true);
      setPlaceholderValue(placeholder || 'Type to search');
      handleOnSelect([], []);
    };

    React.useEffect(() => {
      if (triggerResetToDefaultRef.current === triggerResetToDefault - 1) {
        setPlaceholderValue(initialisedPlaceholderValue);
        // setIsCleared(false);
        handleOnSelect(
          normalisedDefaultValue as string[],
          isPrimitive
            ? normalisedDefaultValue
            : items.filter((item: any) =>
                normalisedDefaultValue.includes(item[keyForValue!]),
              ),
        );
        triggerResetToDefaultRef.current = triggerResetToDefault;
      }
    }, [triggerResetToDefault]);

    // React.useEffect(() => {
    //   if (!isCleared) {
    //     setPlaceholderValue(
    //       calculatePlaceholderValue(
    //         !isPrimitive && normalisedDefaultValue.length > 0
    //           ? items.filter(
    //               (item) => item[keyForValue!] === normalisedDefaultValue[0],
    //             )
    //           : normalisedDefaultValue,
    //       ),
    //     );
    //   }
    // }, [isCleared]);

    // Virtualizer
    const virtualizer = useVirtualizer({
      count: collection.size,
      getScrollElement: () => contentRef.current,
      estimateSize: () => (createDescription ? 48 : 28),
      overscan: 1,
      scrollPaddingEnd: 1,
    });

    const handleScrollToIndexFn = (details: { index: number }) => {
      try {
        if (
          contentRef.current &&
          details.index >= 0 &&
          details.index < collection.size &&
          virtualizer.getVirtualItems().length > 0
        ) {
          virtualizer.scrollToIndex(details.index, {
            align: 'center',
            behavior: 'auto',
          });
        }
      } catch (error) {
        console.warn('Scroll to index failed:', error);
      }
    };

    // Handle clear state when value becomes empty
    // React.useEffect(() => {
    //   if (value.length === 0 && !isCleared && !inputValue) {
    //     setIsCleared(true);
    //     setPlaceholderValue(placeholder || 'Type to search');
    //   }
    // }, [isCleared]);

    // const displayPlaceholder =
    //   isCleared || value.length === 0
    //     ? placeholderValue
    //     : value.length === 1
    //       ? collection.items.find(
    //           (item) =>
    //             String(isPrimitive ? item : item[keyForValue!]) ===
    //             String(value[0]),
    //         )?.[keyForDisplay!] || String(value[0])
    //       : `${value.length} selected`;

    return (
      <ChakraComboBox.Root
        ref={ref}
        color={color}
        collection={collection}
        immediate={true}
        value={value}
        defaultValue={normalisedDefaultValue}
        inputValue={inputValue}
        disabled={disabled || collection.items.length === 0}
        pointerEvents={collection.items.length === 0 ? 'none' : 'auto'}
        scrollToIndexFn={handleScrollToIndexFn}
        openOnClick
        multiple={multiple}
        variant="outline"
        inputBehavior="autocomplete"
        onInputValueChange={handleInputChange}
        onValueChange={handleOnChange}
        onInteractOutside={onBlur}
        {...rest}
      >
        <ChakraComboBox.Control>
          <InputGroup
            display={'flex'}
            startElement={
              startElement || <Icon as={AiOutlineSearch} color={color} />
            }
            endElement={endElement}
          >
            <ChakraComboBox.Input
              _placeholder={labelStyles.placeholder({})}
              placeholder={
                collection.items.length === 0
                  ? 'List is empty'
                  : placeholderValue
              }
            />
          </InputGroup>
          {label && (
            <ChakraComboBox.Label
              {...(labelStyles.floating({}) as any)}
              disabled={disabled}
            >
              {label}
              {infoTip && (
                <Span pointerEvents="auto">
                  <InfoTip>
                    <Alert>{infoTip}</Alert>
                  </InfoTip>
                </Span>
              )}
            </ChakraComboBox.Label>
          )}
          <ChakraComboBox.IndicatorGroup>
            {value.length > 0 && clearable && (
              <ChakraComboBox.ClearTrigger
                onClick={handleClear}
                color={color}
              />
            )}
            <ChakraComboBox.Trigger />
          </ChakraComboBox.IndicatorGroup>
        </ChakraComboBox.Control>
        <Portal>
          <ChakraComboBox.Positioner zIndex={2000}>
            <ChakraComboBox.Content
              ref={contentRef}
              maxW="300px"
              overflowY="auto"
            >
              <ChakraComboBox.Empty>No items found</ChakraComboBox.Empty>
              {useSelectAllOption &&
                multiple &&
                virtualizer.getVirtualItems().length > 0 && (
                  <ChakraComboBox.Item
                    key={'__select_all__'}
                    item={{
                      value: '__select_all__',
                      label: allItemsSelected ? 'Deselect All' : 'Select All',
                    }}
                    onClick={handleSelectAll}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid',
                      borderColor: 'border.subtle',
                    }}
                  >
                    <Stack width="100%" direction="row" align="center">
                      <ChakraComboBox.ItemText color="primary.fg">
                        {allItemsSelected ? 'Deselect All' : 'Select All'}
                      </ChakraComboBox.ItemText>
                      {allItemsSelected && (
                        <Span color="red.500" fontSize="md" fontWeight="bold">
                          ×
                        </Span>
                      )}
                    </Stack>
                    {/* <ChakraComboBox.ItemIndicator /> */}
                  </ChakraComboBox.Item>
                )}
              <div
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {virtualizer.getVirtualItems().map((virtualItem) => {
                  const item = collection.items[virtualItem.index];

                  return (
                    <ChakraComboBox.Item
                      key={`${isPrimitive ? item : item[keyForValue!]}`}
                      item={item}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: `${virtualItem.size}px`,
                        transform: `translateY(${virtualItem.start}px)`,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Stack
                        gap="0"
                        direction="row"
                        align="center"
                        width="100%"
                      >
                        <Stack gap="0" flex="1">
                          <ChakraComboBox.ItemText truncate>
                            <Highlight
                              ignoreCase
                              query={String(value)}
                              styles={{
                                bg: 'yellow.emphasized',
                                fontWeight: 'medium',
                              }}
                            >
                              {isPrimitive ? item : item[keyForDisplay!]}
                            </Highlight>
                          </ChakraComboBox.ItemText>
                          {createDescription && (
                            <Span color="fg.subtle" textStyle="caption.muted">
                              {createDescription(item)}
                            </Span>
                          )}
                        </Stack>
                        <ChakraComboBox.ItemIndicator />
                      </Stack>
                    </ChakraComboBox.Item>
                  );
                })}
              </div>
            </ChakraComboBox.Content>
          </ChakraComboBox.Positioner>
        </Portal>
      </ChakraComboBox.Root>
    );
  },
);

export const ComboBox = React.forwardRef<HTMLDivElement, ComboboxProps>(
  function ComboBox(props, ref) {
    return Array.isArray(props.items) && props.items.length > 0 ? (
      <ComboBox_Inner ref={ref} {...props} />
    ) : (
      <EmptyStateList isLoading={props.isLoading} title="No items to display" />
    );
  },
);

const normaliseDefaultValue = (
  defaultValue: string[] | number[] | string | number,
): string[] => {
  if (Array.isArray(defaultValue)) {
    if (defaultValue.length <= 0) return [];
    return defaultValue.map((value) => String(value));
  }
  if (!String(defaultValue)) return [];
  return [String(defaultValue)];
};
